import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertCircle,
  Check,
  ChevronsUpDown,
  Loader2,
  Save,
  Trash2,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { ClubPicker } from "@/components/ClubPicker";
import { useQuery } from "@tanstack/react-query";

interface MemberOption {
  userId: string;
  userName: string;
}

export interface ScheduledMatchDialogValue {
  id: string;
  leagueId: string | null;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  court?: string | null;
  notes?: string | null;
  team1Player1Id: string;
  team1Player2Id?: string | null;
  team2Player1Id: string;
  team2Player2Id?: string | null;
  homeClubId?: string | null;
  homeClubName?: string | null;
}

interface Props {
  match: ScheduledMatchDialogValue | null;
  members: MemberOption[];
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * Optional callback fired after the scheduled match is successfully
   * deleted. Lets the caller refetch its list / close any nested
   * dialogs. When omitted, the dialog falls back to invalidating the
   * standard query keys only.
   */
  onDeleted?: () => void;
  requireAllPlayers?: boolean;
}

const NONE = "__none__";

/**
 * League-admin dialog for editing an upcoming scheduled match
 * (date/time/location/court/players/notes). Talks to
 * `PATCH /api/scheduled-matches/:id` and invalidates the league + scheduled
 * match caches on success.
 */
export function EditScheduledMatchDialog({
  match,
  members,
  onClose,
  onSuccess,
  onDeleted,
  requireAllPlayers = true,
}: Props) {
  const { toast } = useToast();
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [location, setLocation] = useState("");
  const [court, setCourt] = useState("");
  const [notes, setNotes] = useState("");
  const [t1p1, setT1P1] = useState<string>("");
  const [t1p2, setT1P2] = useState<string>(NONE);
  const [t2p1, setT2P1] = useState<string>("");
  const [t2p2, setT2P2] = useState<string>(NONE);
  const [homeClubId, setHomeClubId] = useState<string | null>(null);
  const [homeClubName, setHomeClubName] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // The picker needs an ISO-2 country to scope its search; pull it
  // from the editor's profile so admins can change a match's club
  // without re-entering their country.
  const { data: profileData } = useQuery<{
    profile?: { country?: string | null } | null;
  }>({
    queryKey: ["/api/profile"],
    enabled: !!match,
  });
  const editorCountry = profileData?.profile?.country ?? null;

  // Re-seed every time a different match is targeted so we don't carry
  // over stale state between edits.
  useEffect(() => {
    if (!match) return;
    setScheduledDate(match.scheduledDate ?? "");
    setScheduledTime(match.scheduledTime ?? "");
    setLocation(match.location ?? "");
    setCourt(match.court ?? "");
    setNotes(match.notes ?? "");
    setT1P1(match.team1Player1Id);
    setT1P2(match.team1Player2Id ?? NONE);
    setT2P1(match.team2Player1Id);
    setT2P2(match.team2Player2Id ?? NONE);
    setHomeClubId(match.homeClubId ?? null);
    setHomeClubName(match.homeClubName ?? null);
    setErrorMsg(null);
  }, [match?.id]);

  // Clear the manually-set inline error as soon as the user changes
  // any field. The live `validationError` memo continues to render
  // independently — this only resets the explicit click-Save flag.
  useEffect(() => {
    if (!errorMsg) return;
    setErrorMsg(null);
  }, [
    errorMsg,
    scheduledDate,
    scheduledTime,
    location,
    court,
    notes,
    t1p1,
    t1p2,
    t2p1,
    t2p2,
  ]);

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  // Allow scheduling up to a year out, matching the schedule-match flow.
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  }, []);

  // Members already chosen elsewhere are disabled in the other selectors so
  // the same person cannot occupy two slots.
  const taken = (selfKey: "t1p1" | "t1p2" | "t2p1" | "t2p2") => {
    const set = new Set<string>();
    if (selfKey !== "t1p1" && t1p1) set.add(t1p1);
    if (selfKey !== "t1p2" && t1p2 && t1p2 !== NONE) set.add(t1p2);
    if (selfKey !== "t2p1" && t2p1) set.add(t2p1);
    if (selfKey !== "t2p2" && t2p2 && t2p2 !== NONE) set.add(t2p2);
    return set;
  };

  const renderMemberSelect = (
    label: string,
    selfKey: "t1p1" | "t1p2" | "t2p1" | "t2p2",
    value: string,
    setValue: (v: string) => void,
    optional: boolean,
  ) => {
    const disabled = taken(selfKey);
    return (
      <div className="space-y-1.5">
        <Label className="text-xs">{label}</Label>
        <SearchableMemberSelect
          selfKey={selfKey}
          members={members}
          value={value}
          onChange={setValue}
          optional={optional}
          disabledIds={disabled}
        />
      </div>
    );
  };

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!match) throw new Error("No match to edit");
      return apiRequest("PATCH", `/api/scheduled-matches/${match.id}`, {
        scheduledDate,
        scheduledTime,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        location: location.trim(),
        court: court.trim() ? court.trim() : null,
        notes: notes.trim() ? notes.trim() : null,
        team1Player1Id: t1p1,
        team1Player2Id: t1p2 === NONE ? null : t1p2,
        team2Player1Id: t2p1,
        team2Player2Id: t2p2 === NONE ? null : t2p2,
        homeClubId: homeClubId ?? null,
      });
    },
    onSuccess: async (response) => {
      if (response.ok) {
        toast({
          title: "Partido actualizado",
          description: "Los participantes recibirán una notificación.",
        });
        // Invalidate everything that depends on this scheduled match.
        if (match?.leagueId) {
          queryClient.invalidateQueries({
            queryKey: ["/api/leagues", match.leagueId],
          });
        }
        queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches"] });
        queryClient.invalidateQueries({
          queryKey: ["/api/scheduled-matches/upcoming"],
        });
        queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
        queryClient.invalidateQueries({
          queryKey: ["/api/notifications/unread-count"],
        });
        onClose();
        onSuccess?.();
      } else {
        const error = await response.json().catch(() => ({}));
        toast({
          title: "Error",
          description:
            error?.message || "No se pudo actualizar el partido",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "No se pudo actualizar el partido",
        variant: "destructive",
      });
    },
  });

  // Mirror the mobile sheet: a single source of truth for whether the
  // form is currently submittable (and, if not, why). Surfacing the
  // reason inline rather than in a toast prevents the silent no-op
  // when the toast renders behind the modal.
  const validationError = useMemo<string | null>(() => {
    if (!scheduledDate || !scheduledTime || !location.trim() || !t1p1 || !t2p1) {
      return "Completa fecha, hora, ubicación y los jugadores principales.";
    }
    if (
      requireAllPlayers &&
      (t1p2 === NONE || !t1p2 || t2p2 === NONE || !t2p2)
    ) {
      return "Debes asignar los 4 jugadores del partido.";
    }
    const proposed = new Date(`${scheduledDate}T${scheduledTime}:00`);
    if (Number.isNaN(proposed.getTime())) {
      return "Fecha u hora inválida.";
    }
    if (proposed.getTime() < Date.now() - 60_000) {
      return "La fecha y hora del partido no pueden ser en el pasado.";
    }
    return null;
  }, [
    scheduledDate,
    scheduledTime,
    location,
    t1p1,
    t1p2,
    t2p1,
    t2p2,
    requireAllPlayers,
  ]);
  const canSave = validationError === null;

  const handleSave = () => {
    if (!match) return;
    if (validationError) {
      setErrorMsg(validationError);
      toast({
        title: "Faltan datos",
        description: validationError,
        variant: "destructive",
      });
      return;
    }
    editMutation.mutate();
  };

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!match) throw new Error("No match to delete");
      return apiRequest("DELETE", `/api/scheduled-matches/${match.id}`);
    },
    onSuccess: async (response) => {
      if (response.ok) {
        toast({
          title: "Partido eliminado",
          description: "El partido agendado fue cancelado correctamente.",
        });
        if (match?.leagueId) {
          queryClient.invalidateQueries({
            queryKey: ["/api/leagues", match.leagueId],
          });
        }
        queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches"] });
        queryClient.invalidateQueries({
          queryKey: ["/api/scheduled-matches/upcoming"],
        });
        queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
        queryClient.invalidateQueries({
          queryKey: ["/api/notifications/unread-count"],
        });
        setConfirmDelete(false);
        onClose();
        onDeleted?.();
      } else {
        const error = await response.json().catch(() => ({}));
        toast({
          title: "Error",
          description:
            error?.message || "No se pudo eliminar el partido",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "No se pudo eliminar el partido",
        variant: "destructive",
      });
    },
  });

  const busy = editMutation.isPending || deleteMutation.isPending;

  if (!match) return null;

  return (
    <Dialog
      open={!!match}
      onOpenChange={(open) => {
        if (!open && !editMutation.isPending) onClose();
      }}
    >
      <DialogContent
        className="max-w-md"
        data-testid="dialog-edit-scheduled-match"
      >
        <DialogHeader>
          <DialogTitle>Editar partido</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="input-edit-scheduled-date" className="text-xs">
                Fecha
              </Label>
              <Input
                id="input-edit-scheduled-date"
                type="date"
                min={todayStr}
                max={maxDate}
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                data-testid="input-edit-scheduled-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="input-edit-scheduled-time" className="text-xs">
                Hora
              </Label>
              <Input
                id="input-edit-scheduled-time"
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                data-testid="input-edit-scheduled-time"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="input-edit-scheduled-location" className="text-xs">
              Ubicación
            </Label>
            <Input
              id="input-edit-scheduled-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Lugar del partido"
              data-testid="input-edit-scheduled-location"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="input-edit-scheduled-court" className="text-xs">
              Cancha (opcional)
            </Label>
            <Input
              id="input-edit-scheduled-court"
              value={court}
              onChange={(e) => setCourt(e.target.value)}
              data-testid="input-edit-scheduled-court"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Club (opcional)</Label>
            <ClubPicker
              country={editorCountry}
              value={homeClubId}
              onChange={(clubId: string | null) => {
                setHomeClubId(clubId);
              }}
              testId="edit-scheduled-club"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Equipo 1
            </Label>
            {renderMemberSelect(
              "Jugador 1",
              "t1p1",
              t1p1,
              setT1P1,
              false,
            )}
            {renderMemberSelect(
              requireAllPlayers ? "Jugador 2" : "Jugador 2 (opcional)",
              "t1p2",
              t1p2,
              setT1P2,
              !requireAllPlayers,
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Equipo 2
            </Label>
            {renderMemberSelect(
              "Jugador 1",
              "t2p1",
              t2p1,
              setT2P1,
              false,
            )}
            {renderMemberSelect(
              requireAllPlayers ? "Jugador 2" : "Jugador 2 (opcional)",
              "t2p2",
              t2p2,
              setT2P2,
              !requireAllPlayers,
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="input-edit-scheduled-notes" className="text-xs">
              Notas (opcional)
            </Label>
            <Textarea
              id="input-edit-scheduled-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas para los jugadores"
              data-testid="input-edit-scheduled-notes"
            />
          </div>

          {/*
            Inline error row — visible inside the modal so it cannot
            get stuck behind the dialog like a toast can. Shown only
            after the user attempts to save with invalid data, or as
            a hint mirroring the live `validationError`.
          */}
          {(errorMsg || validationError) && (
            <div
              className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
              data-testid="text-edit-scheduled-error"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span className="leading-snug">
                {errorMsg ?? validationError}
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="destructive"
            onClick={() => setConfirmDelete(true)}
            disabled={busy}
            data-testid="button-delete-edit-scheduled"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Eliminar partido
          </Button>
          <div className="flex flex-wrap gap-2 justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={busy}
              data-testid="button-cancel-edit-scheduled"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={busy || !canSave}
              data-testid="button-save-edit-scheduled"
            >
              {editMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Guardar cambios
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      <AlertDialog
        open={confirmDelete}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) setConfirmDelete(false);
        }}
      >
        <AlertDialogContent data-testid="dialog-confirm-delete-scheduled">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar partido agendado</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción cancelará el partido y notificará a los jugadores.
              No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleteMutation.isPending}
              data-testid="button-cancel-delete-scheduled"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                deleteMutation.mutate();
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-scheduled"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

interface SearchableMemberSelectProps {
  selfKey: "t1p1" | "t1p2" | "t2p1" | "t2p2";
  members: MemberOption[];
  value: string;
  onChange: (v: string) => void;
  optional: boolean;
  disabledIds: Set<string>;
}

function SearchableMemberSelect({
  selfKey,
  members,
  value,
  onChange,
  optional,
  disabledIds,
}: SearchableMemberSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedMember = useMemo(
    () => members.find((m) => m.userId === value) ?? null,
    [members, value],
  );

  // Manual filtering so the currently selected player can always remain
  // visible regardless of the search term.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members.filter((m) => {
      if (m.userId === value) return false;
      if (!q) return true;
      return m.userName.toLowerCase().includes(q);
    });
  }, [members, value, query]);

  const placeholder = optional ? "Sin asignar" : "Seleccionar jugador";
  const triggerLabel = selectedMember
    ? selectedMember.userName
    : value === NONE && optional
      ? "Sin asignar"
      : placeholder;

  const handleSelect = (next: string) => {
    onChange(next);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !selectedMember && "text-muted-foreground",
          )}
          data-testid={`select-edit-scheduled-${selfKey}`}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar jugador..."
            value={query}
            onValueChange={setQuery}
            data-testid={`input-search-${selfKey}`}
          />
          <CommandList>
            {filtered.length === 0 && !selectedMember && !optional && (
              <CommandEmpty>No se encontraron jugadores</CommandEmpty>
            )}
            {(selectedMember || optional) && (
              <CommandGroup>
                {optional && (
                  <CommandItem
                    value="__none__"
                    onSelect={() => handleSelect(NONE)}
                    data-testid={`option-${selfKey}-none`}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === NONE ? "opacity-100" : "opacity-0",
                      )}
                    />
                    Sin asignar
                  </CommandItem>
                )}
                {selectedMember && (
                  <CommandItem
                    value={selectedMember.userId}
                    onSelect={() => handleSelect(selectedMember.userId)}
                    data-testid={`option-${selfKey}-${selectedMember.userId}`}
                  >
                    <Check className="mr-2 h-4 w-4 opacity-100" />
                    {selectedMember.userName}
                  </CommandItem>
                )}
              </CommandGroup>
            )}
            {filtered.length > 0 && (selectedMember || optional) && (
              <CommandSeparator />
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((m) => {
                  const isDisabled = disabledIds.has(m.userId);
                  return (
                    <CommandItem
                      key={m.userId}
                      value={`${m.userName} ${m.userId}`}
                      disabled={isDisabled}
                      onSelect={() => {
                        if (isDisabled) return;
                        handleSelect(m.userId);
                      }}
                      data-testid={`option-${selfKey}-${m.userId}`}
                    >
                      <Check className="mr-2 h-4 w-4 opacity-0" />
                      <span className="truncate">{m.userName}</span>
                      {isDisabled && (
                        <span className="ml-auto text-xs text-muted-foreground">
                          Ya asignado
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {filtered.length === 0 && (selectedMember || optional) && query && (
              <div className="py-3 px-2 text-center text-xs text-muted-foreground">
                No se encontraron jugadores
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
